<script setup>
import { useRouter } from 'vue-router';

import { Settings } from 'lucide-vue-next';

import { useNotifications } from '@/composables/useNotifications';

const { preferences, updatePreferences } = useNotifications();

const router = useRouter();

const goToFullSettings = () => {
  router.push('/settings/notifications');
};

const toggleEnabled = (val) => {
  updatePreferences({
    enabled: val,
  });
};

const toggleShowToasts = (val) => {
  updatePreferences({
    showToasts: val,
  });
};

const toggleCategoryEnabled = (category, val) => {
  const currentTypes = { ...preferences.value.types };
  currentTypes[category] = {
    ...currentTypes[category],
    enabled: val,
  };
  updatePreferences({ types: currentTypes });
};

const categories = [
  { key: 'sync', label: 'Sync Updates', description: 'Data synchronization status' },
  { key: 'reports', label: 'Reports', description: 'Report generation alerts' },
  { key: 'campaigns', label: 'Campaigns', description: 'Campaign status changes' },
  { key: 'system', label: 'System', description: 'System alerts & announcements' },
  { key: 'amazon_account', label: 'Amazon account', description: 'Amazon Ads / Seller account events' },
];
</script>

<template>
  <VMenu
    :close-on-content-click="false"
    location="bottom end"
    offset="8"
    transition="scale-transition"
  >
    <template #activator="{ props }">
      <button
        class="settings-toggle"
        v-bind="props"
        title="Quick Settings"
      >
        <Settings :size="18" />
      </button>
    </template>

    <VCard width="300" class="quick-settings-card">
      <VCardTitle class="settings-header">
        <span class="settings-title">Quick Settings</span>
      </VCardTitle>

      <VDivider />

      <VCardText class="settings-content">
        <div class="setting-item">
          <div class="setting-info">
            <span class="setting-label">Enable Notifications</span>
            <span class="setting-description">Receive real-time notifications</span>
          </div>
          <VSwitch
            :model-value="preferences.enabled"
            @update:model-value="toggleEnabled"
          />
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <span class="setting-label">Show Toast Popups</span>
            <span class="setting-description">Display toast notifications</span>
          </div>
          <VSwitch
            :model-value="preferences.showToasts"
            :disabled="!preferences.enabled"
            @update:model-value="toggleShowToasts"
          />
        </div>

        <div class="settings-divider" />

        <div class="category-settings">
          <span class="section-label">Categories</span>
          <div
            v-for="category in categories"
            :key="category.key"
            class="setting-item category-item"
          >
            <div class="setting-info">
              <span class="setting-label">{{ category.label }}</span>
            </div>
            <VSwitch
              :model-value="preferences.types[category.key]?.enabled"
              :disabled="!preferences.enabled"
              density="compact"
              @update:model-value="(val) => toggleCategoryEnabled(category.key, val)"
            />
          </div>
        </div>
      </VCardText>

      <VDivider />

      <VCardActions class="settings-footer">
        <VBtn
          block
          variant="tonal"
          color="primary"
          size="small"
          @click="goToFullSettings"
        >
          All Notification Settings
          <VIcon icon="mdi-chevron-right" end size="18" />
        </VBtn>
      </VCardActions>
    </VCard>
  </VMenu>
</template>

<style lang="scss" scoped>
.settings-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgb(var(--v-theme-secondary-text-color));
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: rgb(var(--v-theme-input-background));
    color: rgb(var(--v-theme-text-main-color));
  }
}

.quick-settings-card {
  background: rgb(var(--v-theme-popup-primary)) !important;
  border: 1px solid rgb(var(--v-theme-borders-primary));
  box-shadow: var(--popover-shadow) !important;
}

.settings-header {
  padding: 12px 16px;
  min-height: auto;
}

.settings-title {
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--v-theme-text-color));
}

.settings-content {
  padding: 12px 16px;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;

  &.category-item {
    padding: 2px 0;
  }

  :deep(.v-switch) {
    flex: 0 0 auto;
  }
}

.setting-info {
  flex: 1;
  min-width: 0;
}

.setting-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--v-theme-text-color));
}

.setting-description {
  display: block;
  font-size: 11px;
  color: rgb(var(--v-theme-tertiary-text-color));
  margin-top: 2px;
}

.section-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgb(var(--v-theme-tertiary-text-color));
  margin-bottom: 8px;
  margin-top: 8px;
}

.settings-divider {
  height: 1px;
  background: rgb(var(--v-theme-borders-primary));
  margin: 12px 0;
}

.settings-footer {
  padding: 12px 16px;
}
</style>
